import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Check, X, ChevronDown, ChevronRight, Lock, AlertTriangle, Euro, History, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const eur = (n) => (Number(n) || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });

/** Bloque «Facturación / Importe del trabajo» de un reporte normal: exactamente lo que FD recibirá (utils/albaran_items). */
export function ImporteTrabajo({ rutaId, idx, refreshKey, onEco }) {
  const [eco, setEco] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState("");
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);
  const [openProd, setOpenProd] = useState(false);
  const [openHist, setOpenHist] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/rutas/${rutaId}/paradas/${idx}/economico`);
      setEco(data); onEco?.(data);
    } catch (e) { if (e.response?.status !== 403) toast.error("No se pudo cargar el importe"); setEco(null); onEco?.(null); }
    finally { setLoading(false); }
  }, [rutaId, idx, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setEdit(false); load(); }, [load]);

  const guardar = async () => {
    const n = Number(String(val).replace(",", "."));
    if (Number.isNaN(n) || n < 0) { toast.error("Importe no válido"); return; }
    if (eco.motivo_obligatorio && motivo.trim().length < 3) { toast.error("El reporte ya está aprobado: indica el motivo del cambio"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/rutas/${rutaId}/paradas/${idx}/precio-montaje`, { precio_montaje: n, motivo: motivo.trim() || null });
      toast.success(data.sin_cambios ? "Sin cambios" : `Precio de montaje: ${eur(data.anterior)} → ${eur(data.precio_montaje)}`);
      setEdit(false); setMotivo(""); await load();
    } catch (e) { const d = e.response?.data?.detail; toast.error(d?.msg || d || "No se pudo guardar", { duration: 8000 }); }
    finally { setSaving(false); }
  };

  if (loading && !eco) return <div className="text-[11px] text-zinc-500 flex items-center gap-1" data-testid="importe-cargando"><Loader2 className="w-3 h-3 animate-spin" /> Importe…</div>;
  if (!eco) return null;
  if (eco.es_manual) return null;

  const m = eco.montaje;
  return (
    <div className={`border rounded-lg p-3 space-y-2 ${eco.total_cero ? "border-rose-500/50 bg-rose-500/5" : eco.montaje_cero ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-zinc-900/40"}`} data-testid="importe-trabajo">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold flex items-center gap-1"><Euro className="w-3 h-3" /> Facturación / Importe del trabajo</div>
        {eco.ya_albaranado && <span className="text-[10px] text-emerald-300 inline-flex items-center gap-1" data-testid="importe-albaranado"><Lock className="w-3 h-3" /> Albaranado {eco.ya_albaranado.albaran_numero || eco.ya_albaranado.fd_delivery_note_number || ""}</span>}
      </div>

      {/* Montaje */}
      <div className="flex items-center gap-2 text-xs" data-testid="importe-montaje-row">
        <span className="text-zinc-400 w-24 shrink-0">Montaje</span>
        {!edit ? (
          <>
            {eco.montaje_cero ? (
              <span className="font-mono font-semibold text-amber-300 inline-flex items-center gap-1" data-testid="importe-montaje-pendiente"><AlertTriangle className="w-3.5 h-3.5" /> Importe de montaje pendiente · {eur(0)}</span>
            ) : (
              <span className="font-mono font-semibold text-zinc-100" data-testid="importe-montaje">{eur(m.precio)}</span>
            )}
            {eco.puede_editar && (
              <button type="button" onClick={() => { setVal(String(m.precio || "")); setEdit(true); }} data-testid="editar-importe-btn" title="Editar precio de montaje (fuente real: línea de trabajo)"
                className="ml-auto text-zinc-400 hover:text-amber-300 inline-flex items-center gap-1 text-[11px]"><Pencil className="w-3.5 h-3.5" /> Editar importe</button>
            )}
            {!eco.puede_editar && eco.bloqueo_edicion && <span className="ml-auto text-[10px] text-zinc-500 inline-flex items-center gap-1" title={eco.bloqueo_edicion} data-testid="importe-bloqueado"><Lock className="w-3 h-3" /> bloqueado</span>}
          </>
        ) : (
          <div className="flex-1 flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <Input type="number" min="0" step="0.01" value={val} onChange={(e) => setVal(e.target.value)} autoFocus data-testid="importe-input" className="h-7 w-28 bg-zinc-950 border-amber-500/40 text-xs font-mono" onKeyDown={(e) => { if (e.key === "Enter") guardar(); if (e.key === "Escape") setEdit(false); }} />
              <span className="text-zinc-500">€</span>
              <Button size="sm" onClick={guardar} disabled={saving} data-testid="importe-guardar" className="h-7 px-2 bg-amber-500 hover:bg-amber-400 text-zinc-900"><Check className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="ghost" onClick={() => setEdit(false)} disabled={saving} data-testid="importe-cancelar" className="h-7 px-2"><X className="w-3.5 h-3.5" /></Button>
            </div>
            <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="importe-motivo" placeholder={eco.motivo_obligatorio ? "Motivo del cambio (obligatorio: reporte ya aprobado)" : "Motivo (opcional)"} className="h-7 bg-zinc-950 border-zinc-800 text-[11px]" />
          </div>
        )}
      </div>

      {/* Producción */}
      <div className="text-xs" data-testid="importe-produccion-row">
        <button type="button" onClick={() => setOpenProd((o) => !o)} className="w-full flex items-center gap-2 text-left" data-testid="importe-produccion-toggle">
          <span className="text-zinc-400 w-24 shrink-0">Producción</span>
          <span className="font-mono font-semibold text-zinc-100" data-testid="importe-produccion">{eur(eco.subtotal_produccion)}</span>
          <span className="text-[10px] text-zinc-500">{eco.produccion.length} concepto(s){eco.componentes_omitidos?.length ? ` · ${eco.componentes_omitidos.length} no facturable(s)` : ""}</span>
          <span className="ml-auto text-zinc-500">{openProd ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</span>
        </button>
        {openProd && (
          <ul className="mt-1.5 ml-2 border-l border-zinc-800 pl-2 space-y-1" data-testid="importe-produccion-lista">
            {eco.produccion.map((it) => (
              <li key={it.componente_id} className="flex gap-2 text-[11px]" data-testid={`importe-item-${it.componente_id}`}>
                <span className="flex-1 text-zinc-300 leading-snug">{it.descripcion}</span>
                <span className="font-mono text-zinc-400 shrink-0">{it.cantidad} × {eur(it.precio_unitario)}</span>
                <span className="font-mono text-zinc-100 shrink-0 w-20 text-right">{eur(it.subtotal)}</span>
              </li>
            ))}
            {eco.produccion.length === 0 && <li className="text-[11px] text-zinc-500 italic">Sin componentes facturables</li>}
            {eco.componentes_omitidos?.map((o, i) => <li key={i} className="text-[10px] text-zinc-500" data-testid="importe-omitido">No se factura: {o.nombre || o.componente_id?.slice(0, 6)} ({o.motivo})</li>)}
          </ul>
        )}
      </div>

      {/* Total */}
      <div className="flex items-center gap-2 text-xs pt-2 border-t border-zinc-800">
        <span className="text-zinc-400 w-24 shrink-0">Total a FD</span>
        <span className={`font-mono font-bold text-base ${eco.total_cero ? "text-rose-300" : "text-emerald-300"}`} data-testid="importe-total">{eur(eco.total)}</span>
        <span className="text-[10px] text-zinc-500">sin IVA · {eco.n_conceptos} concepto(s)</span>
        {eco.historial?.length > 0 && (
          <button type="button" onClick={() => setOpenHist((o) => !o)} className="ml-auto text-[10px] text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1" data-testid="importe-historial-toggle"><History className="w-3 h-3" /> {eco.historial.length} cambio(s)</button>
        )}
      </div>
      {eco.total_cero && !eco.ya_albaranado && <div className="text-[11px] text-rose-300 flex items-start gap-1" data-testid="importe-aviso-cero"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Importe total 0 €: el envío a FD pedirá confirmación explícita («Sin cargo») o define el importe.</div>}
      {eco.aviso_montaje_cero && !eco.ya_albaranado && <div className="text-[11px] text-amber-300 flex items-start gap-1" data-testid="importe-aviso-montaje-cero"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Montaje a 0 €: sólo se albaranará la producción. Confirma que el montaje no se factura o define su importe.</div>}
      {eco.fd_confirmado_cero && <div className="text-[10px] text-zinc-400" data-testid="importe-confirmado-cero">Enviado a 0 € «Sin cargo» por {eco.fd_confirmado_cero.usuario} · {new Date(eco.fd_confirmado_cero.fecha).toLocaleString("es-ES")}{eco.fd_confirmado_cero.motivo ? ` · ${eco.fd_confirmado_cero.motivo}` : ""}</div>}
      {openHist && (
        <ul className="text-[10px] text-zinc-400 space-y-0.5" data-testid="importe-historial">
          {[...eco.historial].reverse().map((h, i) => <li key={i}>{new Date(h.fecha).toLocaleString("es-ES")} · {h.usuario} · {eur(h.anterior)} → {eur(h.nuevo)} · {h.origen}{h.motivo ? ` · ${h.motivo}` : ""}</li>)}
        </ul>
      )}
    </div>
  );
}

/** Confirmación fuerte para enviar a FD un reporte con importe total 0 €. */
export function ConfirmarCeroDialog({ open, onClose, onConfirm, loading }) {
  const [txt, setTxt] = useState("");
  const [motivo, setMotivo] = useState("");
  useEffect(() => { if (open) { setTxt(""); setMotivo(""); } }, [open]);
  if (!open) return null;
  const ok = txt.trim() === "0" && motivo.trim().length >= 3;
  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" data-testid="confirmar-cero-dialog">
      <div className="bg-zinc-950 border border-rose-500/40 rounded-xl p-5 w-full max-w-md space-y-3 text-sm">
        <div className="font-bold text-rose-300 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Confirmar envío a 0 €</div>
        <p className="text-xs text-zinc-300">Este reporte no tiene importe facturable (montaje 0 € y sin componentes con precio). Si continúas, se creará una línea de albarán a <b>0,00 € «Sin cargo»</b> en FacturaDirecta y quedará registrado quién lo confirmó. Si el trabajo debe cobrarse, cancela y usa «Editar importe».</p>
        <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo (garantía, cortesía, incluido en presupuesto…)" data-testid="confirmar-cero-motivo" className="h-8 bg-zinc-900 border-zinc-800 text-xs" />
        <Input value={txt} onChange={(e) => setTxt(e.target.value)} placeholder='Escribe "0" para confirmar' data-testid="confirmar-cero-input" className="h-8 bg-zinc-900 border-rose-500/40 text-xs font-mono" />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={loading} data-testid="confirmar-cero-cancelar">Cancelar</Button>
          <Button onClick={() => onConfirm(motivo.trim())} disabled={!ok || loading} data-testid="confirmar-cero-btn" className="bg-rose-500 hover:bg-rose-400 text-zinc-900 font-bold">{loading && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Enviar a 0 € · Sin cargo</Button>
        </div>
      </div>
    </div>
  );
}
